import {
    Button,
    Classes,
    ITextAreaProps,
    Keys,
    MenuItem,
    NonIdealState,
    Popover,
    Position,
    TextArea,
} from "@blueprintjs/core";
import classNames from "classnames";
import { differenceInSeconds, formatDistanceToNowStrict, formatRFC7231 } from "date-fns";
import { BaseEmoji, EmojiData, Picker } from "emoji-mart/dist-modern";
import { JitsiConference, JitsiMeet, JitsiParticipant } from "lib/lib-jitsi-meet";
import { memoize } from "lodash";
import { throttle } from "lodash-es";
import { action, reaction } from "mobx";
import { observer } from "mobx-react";
import React from "react";
import { Mention, MentionItem, MentionsInput, SuggestionDataItem } from "react-mentions";

import { getUserMessageColor, isOnlyEmojis, PrivateChatMessage, renderMessage } from "../../chat";
import { ChatUIState, getToaster, LocalUserState } from "../../common";
import NicknameInput from "./NicknameInput";

import "emoji-mart/css/emoji-mart.css";
import styles from "./ChatRoom.module.css";

export interface ChatRoomProps {
    jitsi: JitsiMeet;
    room: JitsiConference;
    recipient?: JitsiParticipant;
    localUser: LocalUserState;
    uiState: ChatUIState;
    inputProps?: Partial<ITextAreaProps>;
    isSelfViewHidden?: boolean;
    /** ids users who can be @mentioned */
    mentionUserIds?: string[];

    useDarkTheme?: boolean;
    performanceStageTheme?: boolean;
    roomTopic?: string;
    showMessageColors?: boolean;
}

@observer
export default class extends React.PureComponent<ChatRoomProps, never> {
    public static defaultProps: Partial<ChatRoomProps> = {
        isSelfViewHidden: false,
        useDarkTheme: true,
        performanceStageTheme: false,
        showMessageColors: false,
        mentionUserIds: [],
    };

    private getMentionData = memoize((mentionUserIds: string[] = []) => {
        return mentionUserIds.map((userId) => ({
            id: userId,
            display: this.props.room.getParticipantById(userId)?.getDisplayName() as string,
        }));
    });

    // true if we are scrolling the container for the user, in which case we shouldn't react to onScroll events
    private isAutoScrolling = false;

    private containerEl?: HTMLDivElement | null;
    private handleMessagesContainerRef = (el: HTMLDivElement | null) => (this.containerEl = el);

    private toastId?: string;
    private dismissNotificationToast = () => {
        if (this.toastId !== undefined) {
            getToaster().dismiss(this.toastId);
            this.toastId = undefined;
        }
    };

    public componentDidMount() {
        this.scrollToLatestMessages();
        reaction(() => this.props.uiState.messages.length, this.handleNewChatMessage);
    }

    public render() {
        const {
            uiState,
            room,
            localUser,
            recipient,
            isSelfViewHidden,
            performanceStageTheme,
            useDarkTheme,
            roomTopic,
        } = this.props;
        const isPublicChat = recipient === undefined;

        return (
            <div
                className={classNames(styles.container, {
                    [styles.public]: isPublicChat,
                    [styles.private]: !isPublicChat,
                    [styles.performanceStageTheme]: performanceStageTheme,
                    [Classes.DARK]: useDarkTheme,
                })}
            >
                <div
                    className={styles.messagesContainer}
                    ref={this.handleMessagesContainerRef}
                    onScroll={this.handleScroll}
                >
                    {uiState.messages.length === 0 ? (
                        <NonIdealState title="Chat" icon="chat" description="It's quiet in here..." />
                    ) : (
                        this.renderChatMessages()
                    )}
                </div>
                {roomTopic ? <div className={styles.roomTopic}>{roomTopic}</div> : undefined}
                {isSelfViewHidden && (
                    <div className={styles.inlineNicknameInputContainer}>
                        <NicknameInput room={room} localUser={localUser} />
                        <div className={styles.rightAlignedEmojiInput}>{this.maybeRenderEmojiPicker()}</div>
                    </div>
                )}
                {this.renderInput()}
            </div>
        );
    }

    private renderChatMessages = () => {
        const { showMessageColors } = this.props;
        const { messages } = this.props.uiState;

        return messages.map(({ userId, message, userNickname, isSelf, timestamp }, i) => {
            const isLastMessage = i === messages.length - 1;
            const showMessageAuthor = isLastMessage || messages[i + 1]?.userId !== userId;
            const messageColor = showMessageColors && !isSelf ? getUserMessageColor(userId, message) : undefined;

            return (
                <div
                    className={classNames(styles.chatMessageRow, {
                        [styles.chatMessageFromSelf]: isSelf,
                    })}
                    key={i}
                >
                    <div
                        className={classNames(styles.chatMessage, messageColor, {
                            [styles.onlyEmojis]: isOnlyEmojis(message),
                        })}
                        style={{
                            backgroundColor: messageColor,
                        }}
                        dangerouslySetInnerHTML={{ __html: renderMessage(message) }}
                    />
                    {showMessageAuthor && (
                        <>
                            <div className={styles.chatMessageAuthor}>
                                {userNickname}
                                {this.maybeRenderTimestamp(timestamp)}
                            </div>
                        </>
                    )}
                </div>
            );
        });
    };

    private maybeRenderTimestamp(timestamp: Date) {
        if (differenceInSeconds(new Date(), timestamp) < 2) {
            return null;
        }
        return (
            <span className={styles.chatMessageTimestamp} title={formatRFC7231(timestamp)}>
                {formatDistanceToNowStrict(timestamp)} ago
            </span>
        );
    }

    private renderInput() {
        const { inputProps, uiState, performanceStageTheme } = this.props;
        const { inputValue } = uiState;

        if (performanceStageTheme) {
            return (
                <div className={styles.inputContainer}>
                    <MentionsInput
                        className={classNames(
                            styles.chatInput,
                            styles.chatInputWithMentions,
                            Classes.SMALL,
                            Classes.FILL,
                            {
                                [styles.onlyEmojis]: isOnlyEmojis(inputValue),
                            }
                        )}
                        value={inputValue}
                        onChange={this.handleMentionInputChange}
                        onKeyUp={this.sendChatMessage}
                    >
                        <Mention
                            trigger="@"
                            data={this.getMentionData(this.props.mentionUserIds)}
                            renderSuggestion={this.renderSuggestion}
                        />
                    </MentionsInput>
                </div>
            );
        }

        return (
            <div className={styles.inputContainer}>
                <TextArea
                    className={classNames(styles.chatInput, {
                        [styles.onlyEmojis]: isOnlyEmojis(inputValue),
                    })}
                    fill={true}
                    style={{ width: "100%", height: "100%" }}
                    onChange={this.handleMessageInputChange}
                    onKeyUp={this.sendChatMessage}
                    value={inputValue}
                    small={true}
                    {...inputProps}
                />
                {this.maybeRenderEmojiPicker()}
            </div>
        );
    }

    private handleMentionInputChange = (
        event: { target: { value: string } },
        newValue: string,
        newPlainTextValue: string,
        mentions: MentionItem[]
    ) => {
        this.props.uiState.inputValue = newPlainTextValue;
    };

    private renderSuggestion = (
        suggestion: SuggestionDataItem,
        search: string,
        highlightedDisplay: React.ReactNode,
        index: number,
        focused: boolean
    ) => {
        return <MenuItem text={suggestion.display} />;
    };

    private maybeRenderEmojiPicker() {
        const { inputProps, useDarkTheme } = this.props;

        if (inputProps?.disabled) {
            return null;
        }

        return (
            <Popover
                className={styles.emojiPickerTarget}
                position={Position.TOP}
                popoverClassName={classNames(styles.emojiPickerPopover, {
                    [Classes.DARK]: useDarkTheme,
                })}
            >
                <Button text="😃" minimal={true} small={true} />
                <Picker
                    set={navigator.platform.match(/(Mac|iPhone|iPod|iPad)/i) ? "apple" : "twitter"}
                    // @ts-ignore
                    theme={useDarkTheme ? "dark" : undefined}
                    sheetSize={32}
                    showPreview={false}
                    showSkinTones={false}
                    emojiSize={24}
                    emojiTooltip={true}
                    onSelect={this.handleEmojiPickerSelect}
                />
            </Popover>
        );
    }

    // fires after rendering the new message(s)
    private handleNewChatMessage = (numMessages: number) => {
        const { uiState, recipient } = this.props;
        if (this.containerEl != null) {
            const { scrollHeight } = this.containerEl;
            const shouldAutoScroll =
                (recipient !== undefined && uiState.messages[numMessages - 1].isSelf) || !uiState.isUserScrolledUp;

            if (shouldAutoScroll) {
                // if the user hasn't manually scrolled up, we should bring them down
                this.isAutoScrolling = true;
                this.containerEl.scrollTop = scrollHeight;
            } else if (this.toastId === undefined) {
                const message =
                    recipient === undefined ? "New message" : `New message from ${recipient.getDisplayName()}`;
                this.toastId = getToaster().show({
                    message,
                    action: {
                        text: "Show",
                        onClick: () => {
                            action(() => (uiState.isUserScrolledUp = false));
                            this.scrollToLatestMessages();
                            this.dismissNotificationToast();
                        },
                    },
                    onDismiss: this.dismissNotificationToast,
                });
            }
        }
    };

    private handleMessageInputChange = ({ target }: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.props.uiState.inputValue = target.value;
    };

    private sendChatMessage = (event: React.KeyboardEvent) => {
        const { room, recipient, uiState } = this.props;
        const message = (event.target as HTMLTextAreaElement).value;

        if (event.keyCode === Keys.ENTER && !event.altKey && !event.ctrlKey && message.trim() !== "") {
            if (recipient === undefined) {
                room.sendMessage(message.trim());
                uiState.isMessageInFlight = true;
            } else {
                const dataMessage: PrivateChatMessage = {
                    kind: "chatPrivateMessage",
                    from: room.myUserId(),
                    to: recipient.getId(),
                    message,
                };
                room.sendMessage(dataMessage, dataMessage.to);
                // we won't get a message back from the server, so we should update the UI state ourselves
                uiState.messages.push({
                    isSelf: true,
                    message: message.trim(),
                    timestamp: new Date(),
                    userId: room.myUserId(),
                    userNickname: "me",
                });
                uiState.isMessageInFlight = false;
                uiState.inputValue = "";
                // can't scroll to new message here because it hasn't rendered to the DOM yet
            }
        }
    };

    private SCROLL_INTERACTION_TIMEOUT_DELAY_MS = 8000;
    private scrollTimeoutId?: number;
    private handleScroll = throttle(() => {
        if (this.isAutoScrolling) {
            this.isAutoScrolling = false;
        } else if (this.props.uiState.isUserScrolledUp) {
            window.clearTimeout(this.scrollTimeoutId);
            this.scrollTimeoutId = window.setTimeout(
                action(() => (this.props.uiState.isUserScrolledUp = false)),
                this.SCROLL_INTERACTION_TIMEOUT_DELAY_MS
            );
        } else {
            this.props.uiState.isUserScrolledUp = true;
        }
    }, 200);

    private scrollToLatestMessages = () => {
        if (this.containerEl != null) {
            this.containerEl.scrollTop = this.containerEl.scrollHeight;
        }
    };

    private handleEmojiPickerSelect = (emoji: EmojiData) => {
        if ((emoji as BaseEmoji).native !== undefined) {
            this.props.uiState.inputValue += (emoji as BaseEmoji).native;
        }
    };
}
